/**
 * Appliance-bootstrap route contracts (design 5.3.1): one endpoint per
 * frontend-consumed (method, path) on the bootstrap surface. The worker
 * registers its handlers through these
 * (worker/src/features/appliance_bootstrap/api.ts); migrated frontend
 * consumers (features/appliance_apps components: BootstrapBundleStatus,
 * ApplianceDownloadRequestModal) invoke them cross-owner through their
 * declared codeDeps. The legacy Appliances/AppliancesV2 page shells and
 * pages/settings/ApplianceEncryptionSettings.jsx keep their raw calls
 * until they migrate.
 *
 * Same-path method pairs (GET + POST /api/appliance-bootstrap) are
 * contracted separately — contracts are method-aware.
 */

import { defineContracts, endpoint } from '../featureModule'
import { identity } from './appliance_bootstrap'

export const contracts = defineContracts(identity, {
  /** GET /api/disk-encryption-config — has admin provisioned encryption? */
  getDiskEncryptionConfig: endpoint('GET', '/api/disk-encryption-config', { entitlement: 'appliance_bootstrap' }),
  /** POST /api/disk-encryption-config/import — import the sealed encryption bundle. */
  importDiskEncryptionConfig: endpoint('POST', '/api/disk-encryption-config/import', { entitlement: 'appliance_bootstrap' }),

  /** POST /api/bootstrap-bundle/import — import a sealed per-appliance bootstrap bundle. */
  importBootstrapBundle: endpoint('POST', '/api/bootstrap-bundle/import', { entitlement: 'appliance_bootstrap' }),
  /** GET /api/bootstrap-bundle/:agentId — latest bundle status for an agent. */
  getBootstrapBundle: endpoint('GET', '/api/bootstrap-bundle/:agentId', { entitlement: 'appliance_bootstrap' }),

  /** GET /api/appliance-bootstrap — list bootstraps (latest per agent). */
  listBootstraps: endpoint('GET', '/api/appliance-bootstrap', { entitlement: 'appliance_bootstrap' }),
  /** POST /api/appliance-bootstrap — start a bootstrap for an appliance. */
  startBootstrap: endpoint('POST', '/api/appliance-bootstrap', { entitlement: 'appliance_bootstrap' }),
  /** GET /api/appliance-bootstrap/:id — bootstrap detail + phase log. */
  getBootstrap: endpoint('GET', '/api/appliance-bootstrap/:id', { entitlement: 'appliance_bootstrap' }),
  /** POST /api/appliance-bootstrap/:id/phase/:phase — (re)trigger a phase. */
  retryBootstrapPhase: endpoint('POST', '/api/appliance-bootstrap/:id/phase/:phase', { entitlement: 'appliance_bootstrap' }),
  /** POST /api/appliance-bootstrap/:id/cancel — cancel an active bootstrap. */
  cancelBootstrap: endpoint('POST', '/api/appliance-bootstrap/:id/cancel', { entitlement: 'appliance_bootstrap' }),

  /** POST /api/appliance-download-requests — email an OVA download link. */
  requestApplianceDownload: endpoint('POST', '/api/appliance-download-requests', { entitlement: 'appliance_bootstrap' }),
  /** POST /api/appliance-bootstrap-bundle-requests — re-request a bundle from admin. */
  requestBootstrapBundle: endpoint('POST', '/api/appliance-bootstrap-bundle-requests', { entitlement: 'appliance_bootstrap' }),
  /** GET /api/appliance-edge-status — sanitized per-appliance provisioning state. */
  getEdgeStatus: endpoint('GET', '/api/appliance-edge-status', { entitlement: 'appliance_bootstrap' }),

  // ---- App catalog (appliance app-install UI) ------------------------------
  // Served by appCatalogHandler behind the '/api/app-catalog' prefix; the
  // exact/param contracts below outrank the prefix (gate byte-identical).
  /** GET /api/app-catalog — the installable app catalog. */
  listAppCatalog: endpoint('GET', '/api/app-catalog', { entitlement: 'appliance_bootstrap' }),
  /** GET /api/app-catalog/:appCode — one catalog app's descriptor. */
  getAppCatalogApp: endpoint('GET', '/api/app-catalog/:appCode', { entitlement: 'appliance_bootstrap' }),
  /** GET /api/app-catalog/:appCode/schema — an app's install-config JSON schema. */
  getAppCatalogSchema: endpoint('GET', '/api/app-catalog/:appCode/schema', { entitlement: 'appliance_bootstrap' }),
  /** GET /api/app-catalog/:appCode/field-options/:field — the allowed values of a config field picked from a list (?repository= for git-folders). */
  getAppCatalogFieldOptions: endpoint('GET', '/api/app-catalog/:appCode/field-options/:field', { entitlement: 'appliance_bootstrap' }),

  // ---- LUKS keys (Settings → Appliance Encryption) -------------------------
  /** GET /api/appliance-luks-keys — escrowed LUKS keys across appliances. */
  listLuksKeys: endpoint('GET', '/api/appliance-luks-keys', { entitlement: 'appliance_bootstrap' }),
  /** POST /api/appliance-luks-keys/:agentId/revoke — revoke an appliance's escrowed key. */
  revokeLuksKey: endpoint('POST', '/api/appliance-luks-keys/:agentId/revoke', { entitlement: 'appliance_bootstrap' }),

  // ---- Decommissions (AppliancesV2 shell) ----------------------------------
  /** GET /api/appliance-decommissions — decommission job list. */
  listApplianceDecommissions: endpoint('GET', '/api/appliance-decommissions', { entitlement: 'appliance_bootstrap' }),
})
